import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useApplyPromoCodeMutation } from '~services/api/credits';
import type {
	TShopPromoCodeDiscountItem,
	TShopPromoCodeResponse,
} from '~services/api/credits/types';

import {
	SDiscount,
	SForm,
	SInfoText,
	SInput,
	SInputOuter,
} from './PromoCodeForm.styles';

type TPromoCodeForm = {
	PromoCode: string;
	sku: string;
};

type TPromoCodeFormProps = {
	onSuccess: (data: TShopPromoCodeResponse) => void;
	sku: string;
	defaultPromocode?: string;
};

const customErrorConfig: Record<string, string> = {
	NotApplicable: "This code can't be applied to this product.",
	Expired: 'This code has expired.',
	Consumed: 'You have already used this code.',
	NotFound: 'Please use a valid code.',
};

function checkIfApplicable(sku: string, prices: TShopPromoCodeDiscountItem[]) {
	const data = sku.replace('com.ai.web.', '');
	let type = data.split('.')[0];
	type = type.indexOf('coinpack') !== -1 ? 'coinpack' : type;
	const promoSkus = prices.map(
		({ Sku }) => Sku.replace('com.ai.web.', '').split('.')[0]
	);
	return promoSkus.some((item) => item === type || item.includes(type));
}
export function isErrorWithMessage(
	error: unknown
): error is { data: { Error: string } } {
	if (typeof error === 'object' && error !== null && 'data' in error) {
		const data = (error as { data?: unknown }).data;
		return typeof data === 'object' && data !== null && 'Error' in data;
	}
	return false;
}
export const PromoCodeForm = ({
	onSuccess,
	sku,
	defaultPromocode,
}: TPromoCodeFormProps) => {
	const [discount, setDiscount] = useState<number>(0);
	const {
		watch,
		register,
		setError,
		formState: { errors, isSubmitting, isValid, isSubmitSuccessful },
		handleSubmit,
		clearErrors,
		setValue,
	} = useForm<TPromoCodeForm>({
		mode: 'onBlur',
		reValidateMode: 'onSubmit',
		defaultValues: {
			PromoCode: defaultPromocode,
		},
	});
	const promoCode = watch('PromoCode', '');
	const readyForSubmit = isValid && !isSubmitSuccessful;
	const hasData = promoCode.length > 0;
	const hasServerErrors = errors.root?.serverError;
	const hasError = (hasData && !!errors.PromoCode) || !!hasServerErrors;

	let infoText = 'Enter promo code.';

	if (hasError) {
		infoText = errors.root?.serverError?.message ?? 'Please use a valid code.';
	}

	if (isSubmitSuccessful) {
		infoText = errors.root?.serverError?.message ?? 'Applied successfully!';
	} else if (readyForSubmit) {
		infoText = 'Apply promo code.';
	}

	const [send] = useApplyPromoCodeMutation();
	const onSubmit: SubmitHandler<TPromoCodeForm> = async (data) => {
		try {
			const resp = await send(data).unwrap();
			onSuccess(resp);
			setValue('PromoCode', data.PromoCode);
			if (!checkIfApplicable(sku, resp.Prices)) {
				setError('root.serverError', {
					type: 'NotFound',
					message: customErrorConfig.NotApplicable,
				});
				setDiscount(0);
				return;
			}
			clearErrors('PromoCode');
			setDiscount(resp?.DiscountPercent ?? 0);
		} catch (err) {
			setValue('PromoCode', data.PromoCode, { shouldDirty: true });
			if (!isErrorWithMessage(err)) {
				setError('root.serverError', {});
				return;
			}

			const customError = customErrorConfig[err.data.Error];
			if (customError) {
				setError('root.serverError', {
					type: 'NotFound',
					message: customError,
				});
				return;
			}

			setError('root.serverError', {});
		}
	};

	return (
		<SForm onSubmit={handleSubmit(onSubmit)}>
			<SInputOuter data-value={promoCode}>
				<SInput
					autoFocus
					type="text"
					placeholder="XXXXXXXX"
					maxLength={16}
					disabled={isSubmitting || isSubmitSuccessful}
					{...register('PromoCode', {
						required: true,
						maxLength: 16,
						minLength: 6,
						pattern: /^[A-Za-z0-9]+$/,
						onChange: () => {
							if (hasError) {
								clearErrors('root.serverError');
								clearErrors('PromoCode');
							}
						},
					})}
				/>
				{discount > 0 && !hasError ? (
					<SDiscount size="small">
						— <span>{discount}% off</span>
					</SDiscount>
				) : null}
			</SInputOuter>

			<SInfoText
				onClick={readyForSubmit ? handleSubmit(onSubmit) : undefined}
				hasError={hasData && hasError && !readyForSubmit}
				isActive={readyForSubmit}
				size="small"
				weight={readyForSubmit ? 600 : 400}
			>
				{infoText}
			</SInfoText>
		</SForm>
	);
};
